import { useSelector } from '@xstate/react'
import dynamic from 'next/dynamic'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { media } from 'utils/media'
import { BackButton } from 'lib/Button/BackButton'
import { LoadingDots } from 'lib/LoadingDots'
import { ModalPopupNew } from 'lib/ModalPopupNew'
import { Tags } from './machines/authenticationMachine'
import { useAuthentication } from './useAuthentication'

const StyledModal = styled(ModalPopupNew)`
  padding-top: 8px;
`

const LoadingPlaceholder = styled.div`
  margin-top: 16px;
  padding: 48px;
  width: 426px;
  ${media.phoneDown} {
    width: 100%;
  }
`

const LoadingContent = () => (
  <LoadingPlaceholder>
    <LoadingDots fill />
  </LoadingPlaceholder>
)

const NewSessionContent = dynamic(() => import('./components/NewSessionContent').then((res) => res.NewSessionContent), { loading: LoadingContent })

export const NewSessionModal = () => {
  const { authenticationService } = useAuthentication()

  const currentMode = useSelector(authenticationService, (state) => state.context.mode)
  const copyContext = useSelector(authenticationService, (state) => state.context.copyContext)
  const canShowModal = useSelector(authenticationService, (state) => !state.tags.has(Tags.Disabled))
  const modalOpen = useSelector(authenticationService, (state) => state.context.modalOpen)
  const signinLinkSent = useSelector(authenticationService, (state) => state.matches('emailLinkSent'))
  const confirmSignInWithExistingProvider = useSelector(
    authenticationService,
    (state) => state.matches('readyToSignInWithExistingProvider') || state.matches('signingInWithExistingProvider')
  )

  const modalTitle = useMemo(() => {
    if (signinLinkSent || confirmSignInWithExistingProvider) {
      return (
        <BackButton
          onClick={() => {
            authenticationService.send({ type: 'START_OVER' })
          }}
        />
      )
    }

    let postTitle = ''

    switch (copyContext) {
      case 'thingdrops':
        postTitle = 'to participate in our Thingdrop program'
        break
      case 'reviews':
        postTitle = 'to write reviews'
        break

      case 'lists':
        postTitle = "to keep track of brands you've tested or want to test"
        break

      case 'lists-tested':
        postTitle = "to keep track of brands you've tested"
        break

      case 'lists-want-to-test':
        postTitle = 'to keep track of brands you want to test'
        break

      case 'submit-brand':
        postTitle = 'to submit brands to the directory'
        break

      case 'newsletter':
        postTitle = 'to receive our weekly newsletter'
        break

      case 'clap':
        postTitle = 'to clap this review'
        break

      case 'magazine':
        postTitle = 'to read the magazine'
        break
      case 'sampling':
        postTitle = 'to test free products'
        break
    }

    if (currentMode === 'login') {
      return `Log in ${postTitle}`.trim()
    } else {
      return `Create an account ${postTitle}`.trim()
    }
  }, [signinLinkSent, confirmSignInWithExistingProvider, copyContext, currentMode, authenticationService])

  return (
    <>
      {canShowModal && (
        <StyledModal size="fit" title={modalTitle} open={modalOpen} onRequestClose={() => authenticationService.send({ type: 'HIDE_MODAL' })}>
          <NewSessionContent />
        </StyledModal>
      )}
    </>
  )
}
